/* ── 213B §11 [RULED 44] — THE ADDRESSEES OF A PRO FORMA ON A CASE WITH SEVERAL CLIENTS ──
 *
 * Laurent, 29/09: « when a NON collective case has two clients (eg a company and
 * its CEO as a physical person) one needs to be able to determine that the pro
 * forma will be sent to either, or all ». Asked, he chose: all ticked names on
 * the document, with the first ticked client's address; only the first client
 * ticked when the window opens.
 *
 * The window's client rows are untyped (`cl` is `any[]` in ProFormaModal.tsx,
 * whose ESLint count may not rise), so everything it needs of them is read
 * HERE through `unknown` (213 addendum 3 (b)): the rows as addressees, the
 * ticked ones in the case's order, and the ONE join helper for the addressee
 * line — « SA X et M. Y » · « SA X en M. Y » · « SA X and M. Y », « A, B et C »
 * for three [ADVISOR]. What the server keeps is `addressees: [{ clientId,
 * name, email }]` inside `clientAddressSnapshot` (docs/213b-deploy-note.md
 * §8.11); its readers are server/src/lib/invoiceAddressees.js. No function-type
 * parameter here (the base `no-unused-vars` names them): the window hands the
 * NAMES it resolved beside the rows. */


// 213B §11 — the snapshot's `addressees` are READ in src/lib/financialsClient.ts (which recordings/reports-wire
// transpiles ALONE as a data: URL, so that module keeps no import of its own) and re-exported here under the names
// the window and the recording use.
import { storedAddressees, firstAddresseeNameOf, customerNameField } from "./financialsClient";
import type { Addressee } from "./financialsClient";
export { storedAddressees, firstAddresseeNameOf, customerNameField };
export type { Addressee };

/** Several names as one addressee line, joined by `and` — the document's language's own word, read from the seed
 *  (`proforma.addressees.join`: « et » · « en » · « and », through `tIn` in the window) without an Oxford comma:
 *  « A et B », « A, B et C »; one name alone; none → "". */
export function joinNames(names: readonly string[], and: string): string {
  const list = names.map((n) => n.trim()).filter(Boolean);
  if (!list.length) return "";
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(", ")} ${and} ${list[list.length - 1]}`;
}

/** The window's client list as rows: an untyped list handed over as `unknown` (never an `any[]` argument, which the
 *  ratchet counts), or nothing. */
export function asRows(list: unknown): unknown[] {
  return Array.isArray(list) ? (list as unknown[]) : [];
}

/** A client row's id, read through `unknown`. */
export function clientIdOf(row: unknown): number | null {
  const id = (row as { id?: unknown } | null | undefined)?.id;
  return typeof id === "number" && Number.isFinite(id) ? id : null;
}

/** A client row's e-mail, trimmed, read through `unknown`. */
export function clientEmailOf(row: unknown): string {
  const e = (row as { email?: unknown } | null | undefined)?.email;
  return typeof e === "string" ? e.trim() : "";
}


/** The ids ticked when the window opens, or as an edit left them: the stored `addressees` of an unsent pro forma
 *  (an edit), else the first client alone (the ruling). `stored` is the saved snapshot, read through `unknown`. */
export function initialTickedIds(clientsIn: unknown, stored: unknown): number[] {
  const clients = asRows(clientsIn);
  const known = new Set(clients.map(clientIdOf).filter((id): id is number => id != null));
  const kept = storedAddressees(stored).map((a) => a.clientId).filter((id) => known.has(id));
  if (kept.length) return kept;
  const first = clients.length ? clientIdOf(clients[0]) : null;
  return first == null ? [] : [first];
}

/** The ticked clients in the CASE's order (never the order of ticking). */
export function tickedInOrder(clientsIn: unknown, ticked: readonly number[]): unknown[] {
  const clients = asRows(clientsIn);
  const set = new Set(ticked);
  return clients.filter((c) => { const id = clientIdOf(c); return id != null && set.has(id); });
}

/** The addressees as the snapshot stores them: the ticked rows beside their names in the document's language (the
 *  window resolves the names with its own `pname`, in the same order). */
export function addresseesOf(ticked: readonly unknown[], names: readonly string[]): Addressee[] {
  return ticked.map((c, i) => ({ clientId: clientIdOf(c) ?? 0, name: names[i] ?? "", email: clientEmailOf(c) })).filter((a) => a.clientId > 0);
}

/** The index, in the case's order, of the first ticked client — 0 when none is ticked (the window then refuses the
 *  create and says so, but still previews the first client). */
export function firstTickedIndexOf(clientsIn: unknown, ticked: readonly number[]): number {
  const clients = asRows(clientsIn);
  const set = new Set(ticked);
  const i = clients.findIndex((c) => { const id = clientIdOf(c); return id != null && set.has(id); });
  return i < 0 ? 0 : i;
}

/** The boxes' rows: each client's id beside its name (resolved by the window, in the same order). */
export function clientRowsOf(clientsIn: unknown, names: readonly string[]): { id: number; name: string }[] {
  return asRows(clientsIn).map((c, i) => ({ id: clientIdOf(c) ?? 0, name: names[i] ?? "" })).filter((r) => r.id > 0);
}

/** A box toggled: the id added or removed. */
export function toggleId(ticked: readonly number[], id: number): number[] {
  return ticked.includes(id) ? ticked.filter((x) => x !== id) : [...ticked, id];
}

/** The snapshot of the pro forma an edit reopened (`pfMod.doc`), read through `unknown`; null on a create. */
export function editSnapshotOf(mod: unknown): unknown {
  const m = mod as { editId?: unknown; doc?: unknown } | null | undefined;
  if (!m?.editId || !m.doc || typeof m.doc !== "object") return null;
  return (m.doc as { clientAddressSnapshot?: unknown }).clientAddressSnapshot ?? null;
}

/** The names of the ticked clients a saved pro forma holds WITHOUT an e-mail — the draft will not reach them, and
 *  the window says so before sending. */
export function missingEmailsOf(invoice: unknown): string[] {
  const snap = (invoice as { clientAddressSnapshot?: unknown } | null | undefined)?.clientAddressSnapshot;
  return storedAddressees(snap).filter((a) => !a.email).map((a) => a.name || `#${String(a.clientId)}`);
}


